"use client";

import { ChevronDown as ArrowDownIcon } from "lucide-react";
import Flag from "@components/Flag";
import { Listbox } from "@headlessui/react";
import { useCallback } from "react";

import { FEATURE_FLAGS } from "@/config/featureFlags";
import { cn } from "@/lib/utils";
import type { Market } from "@features/market/config/i18n-config";
import {
  findMarketByCode,
  getMarketSwitchHref,
  marketsByCountryName,
} from "@features/market/config/market-config";

import { useQueryClient } from "@tanstack/react-query";
import MarketSelectorOption from "./MarketSelectorOption";
import { useMarketSelector } from "./useMarketSelector";

export default function MarketSelectorDropdown({
  classNames,
  disabled = false,
}: {
  filterAction?: (market: Market) => void;
  disabled?: boolean;
  classNames?: {
    wrapper?: string;
    list?: string;
    input?: string;
  };
}) {
  const queryClient = useQueryClient();
  const { state, onSave } = useMarketSelector();

  const value = state.market;

  const markets = marketsByCountryName();

  const onChange = useCallback(
    (payload: Market) => {
      if (!payload) {
        return;
      }

      const link = getMarketSwitchHref(
        state.market,
        findMarketByCode(payload.countryCode),
      );
      onSave({ link: link });
    },
    [onSave, state.market],
  );

  if (FEATURE_FLAGS.marketSelector.new) {
    const onClick = () =>
      queryClient.setQueryData(["market"], {
        open: true,
        type: "manual",
      });
    return (
      <button
        onClick={onClick}
        className={cn(
          "relative flex h-full items-center space-x-2 px-2 uppercase",
          classNames?.input,
        )}
      >
        <Flag
          code={value.countryCode}
          className="h-3 w-3 rounded-full border border-black object-cover"
        />
        <span className="flex-1 text-left">{value.countryCode}</span>
        <ArrowDownIcon strokeWidth={1} className="size-4" />
      </button>
    );
  }

  return (
    <Listbox
      onChange={onChange}
      by="countryCode"
      as="div"
      disabled={disabled}
      className={classNames?.wrapper || "relative z-50 h-full"}
    >
      <Listbox.Options
        className={cn([
          "absolute top-full right-0 w-full min-w-80 -translate-y-px divide-y border bg-white text-base",
          classNames?.list,
        ])}
      >
        {markets.map((market) => {
          return (
            <MarketSelectorOption
              key={market.countryCode}
              as={Listbox.Option}
              market={market}
              value={market}
              className="ui-selected:bg-gray-100 ui-active:bg-gray-100"
            />
          );
        })}
      </Listbox.Options>
      <Listbox.Button
        className={cn(
          classNames?.input ||
            "ui-not-open:border-transparent ui-not-open:bg-transparent relative flex h-full items-center space-x-2 border-x border-t bg-transparent bg-white px-2 uppercase",
          {
            "disabled:cursor-not-allowed disabled:opacity-50": disabled,
          },
        )}
      >
        <Flag
          code={value.countryCode}
          className="h-3 w-3 rounded-full border border-black object-cover"
        />
        <span className="flex-1 text-left">{value.countryCode}</span>
        <ArrowDownIcon strokeWidth={1} className="size-4" />
      </Listbox.Button>
    </Listbox>
  );
}
